import * as React from "react";
import * as ReactQuery from "react-query";
import axios from "axios";

import {
  InitialStateMinimumRequirements_GraphQl,
  LegacyMagento_ShippingInformation_CartAddressInput,
} from "../../../Common";

import { DataMutation_ShippingSetAuthorityToLeave_QueryKey } from "./data-mutation--shipping-set-authority-to-leave--query-key-factory";

export interface DataMutationShippingSetAuthorityToLeaveConsumerInterface {
  data: DataMutationShippingSetAuthorityToLeave_Result["data"];
  status: DataMutationShippingSetAuthorityToLeave_Result["status"];
}
const defaultValue =
  {} as unknown as DataMutationShippingSetAuthorityToLeaveConsumerInterface;
const DataMutationShippingSetAuthorityToLeaveContext =
  React.createContext(defaultValue);

export interface DataMutationShippingSetAuthorityToLeave_ControllerInterface {
  mutate: DataMutationShippingSetAuthorityToLeave_Result["mutate"];
  mutateAsync: DataMutationShippingSetAuthorityToLeave_Result["mutateAsync"];
}
const controllerDefaultValue =
  {} as unknown as DataMutationShippingSetAuthorityToLeave_ControllerInterface;
const DataMutationShippingSetAuthorityToLeave_ControllerContext =
  React.createContext(controllerDefaultValue);

export interface DataMutationShippingSetAuthorityToLeave_Request {
  cartId: string;
  address: LegacyMagento_ShippingInformation_CartAddressInput;
}
export interface DataMutationShippingSetAuthorityToLeave_Response {
  done: boolean;
}
export type DataMutationShippingSetAuthorityToLeave_Result =
  ReactQuery.UseMutationResult<
    DataMutationShippingSetAuthorityToLeave_Response,
    unknown,
    DataMutationShippingSetAuthorityToLeave_Request,
    unknown
  >;

export interface DataMutationShippingSetAuthorityToLeaveProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_GraphQl;
  };
}
export const DataMutationShippingSetAuthorityToLeaveProvider: React.FunctionComponent<
  DataMutationShippingSetAuthorityToLeaveProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const mutationKey: ReactQuery.MutationKey = React.useMemo(() => {
    return DataMutation_ShippingSetAuthorityToLeave_QueryKey.shippingSetAuthorityToLeave();
  }, []);

  const mutationFn: ReactQuery.MutationFunction<
    DataMutationShippingSetAuthorityToLeave_Response,
    DataMutationShippingSetAuthorityToLeave_Request
  > = React.useCallback(
    async (request) => {
      const headers = {
        "Content-Type": "application/json",
      };

      const { data } = await axios.post(
        `/rest/${initialState.store_code}/V1/guest-carts/${request.cartId}/shipping-information`,
        {
          addressInformation: {
            shipping_address: request.address,
            extension_attributes: {
              authority_to_leave: true,
            },
          },
        },
        { headers }
      );

      if (data.error || data.errors) {
        const firstErrorMessage =
          (!!data.error ? data.error : "") ||
          (data.errors.length > 0
            ? !!data.errors[0].message
              ? data.errors[0].message
              : ""
            : "") ||
          "Unknown Error";

        throw new Error(firstErrorMessage);
      }

      return {
        done: true,
      };
    },
    [initialState]
  );

  const mutation = ReactQuery.useMutation(mutationKey, mutationFn);

  const value: DataMutationShippingSetAuthorityToLeaveConsumerInterface =
    React.useMemo(
      () => ({
        data: mutation.data,
        status: mutation.status,
      }),
      [mutation.data, mutation.status]
    );
  const controllerValue: DataMutationShippingSetAuthorityToLeave_ControllerInterface =
    React.useMemo(
      () => ({
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
      }),
      [mutation, mutation.mutateAsync]
    );
  return (
    <DataMutationShippingSetAuthorityToLeaveContext.Provider value={value}>
      <DataMutationShippingSetAuthorityToLeave_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children || []}
      </DataMutationShippingSetAuthorityToLeave_ControllerContext.Provider>
    </DataMutationShippingSetAuthorityToLeaveContext.Provider>
  );
};

export const useDataMutationShippingSetAuthorityToLeave = () => {
  const context = React.useContext(
    DataMutationShippingSetAuthorityToLeaveContext
  );
  if (context === defaultValue) {
    throw new Error(
      "useDataMutationShippingSetAuthorityToLeave must be used within a DataMutationShippingSetAuthorityToLeaveProvider"
    );
  }
  return context;
};

export const useDataMutationShippingSetAuthorityToLeave_asController = () => {
  const context = React.useContext(
    DataMutationShippingSetAuthorityToLeave_ControllerContext
  );
  if (context === controllerDefaultValue) {
    throw new Error(
      "useDataMutationShippingSetAuthorityToLeave_asController must be used within a DataMutationShippingSetAuthorityToLeaveProvider"
    );
  }
  return context;
};
